<script setup lang="ts">
import {
    alertMessageEmptyHappy,
    alertMessageHappy,
    showAlertMessageHappy
} from '@/happyTheme/event/dashboard/alert/AlertMessageHappy'
import {
    alertDeleteCustomerMessageHappy
} from '@/happyTheme/event/dashboard/user/alertDeleteCustomerMessageHappy'
import { requestDeleteCustomer } from '@/happyTheme/event/dashboard/user/requestDeleteCustomer'
import { TimeOutClass } from '@/happyTheme/services/timeout/TimeOutClass'
import { UserService } from '@/services/UserService'
import customer from '@/storage/customer'
import { tokenSessionId } from '@/storage/setting/tokenSessionId'
import stores from '@/storage/stores'
import { encryptPassword } from '@/utils/account/encryptPassword'
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { AlertTriangle, X, Eye, EyeOff, Trash2 } from 'lucide-vue-next'

const { t } = useI18n()

const isVisible = ref(false)
const password = ref<string | null>(null)
const showPassword = ref(false)
const router = useRouter()

function cancelRequestDeleteCustomer() {
    isVisible.value = false

    const timeOutClass = TimeOutClass.getInstance()
    const idTimeout = setTimeout(() => {
        clearTimeout(idTimeout)
        requestDeleteCustomer.value = false
    }, 250)
    timeOutClass.setIdTimeout(idTimeout)
}

async function useDeleteCustomer() {
    if (!password.value || password.value.length === 0) {
        alertMessageHappy.value = {
            title: t('deleteAccountRequest'),
            class: 'error',
            message: t('deleteAccountConfirmText'),
            errors: []
        }
        const timeOutClass = TimeOutClass.getInstance()
        const showTimeOut = setTimeout(() => {
            showAlertMessageHappy.value = true
        }, 100)
        timeOutClass.setTimeoutWithKey('delete', showTimeOut)

        const idTimeOut = setTimeout(() => {
            showAlertMessageHappy.value = false
            setTimeout(() => {
                alertMessageHappy.value = alertMessageEmptyHappy
            }, 500)
        }, 2000)

        timeOutClass.setIdTimeout(idTimeOut)

        return
    }

    const store = stores.getStoresData()
    const currentCustomer = customer.getCustomerData()

    if (
        (!currentCustomer || !currentCustomer.customer_id) &&
        (!store || !store.store_id)
    ) {
        return
    }
    const passwordEncrypted = await encryptPassword(password.value)

    const storeId = store.store_id

    const userService = new UserService()
    userService.deleteCustomer(
        {
            token_session_id: tokenSessionId.value.token_session_id,
            store_id: storeId,
            customer_id: currentCustomer.customer_id,
            password: passwordEncrypted
        },
        router
    )
}

watch(
    () => requestDeleteCustomer.value,
    (newValue) => {
        if (newValue) {
            setTimeout(() => {
                isVisible.value = true
            }, 10)
        } else {
            isVisible.value = false
        }
    },
    { immediate: true }
)

watch(
    () => alertDeleteCustomerMessageHappy.value,
    (newValue) => {
        if (newValue && newValue.message) {
            setTimeout(() => {
                isVisible.value = true
            }, 10)
        } else {
            isVisible.value = false
        }
    },
    { immediate: true }
)
</script>

<template>
    <section class="section-delete-customer-happy">
        <section class="delete-customer-overlay"></section>

        <Transition
            name="delete-customer"
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <section
                class="delete-customer-card"
                v-if="isVisible"
                role="alertdialog"
                aria-modal="true"
                :aria-label="t('deleteAccountRequest')"
            >
                <!-- Üst danger şeridi -->
                <span class="dcc-topbar" aria-hidden="true"></span>

                <!-- Kapat -->
                <button
                    type="button"
                    class="dcc-close"
                    :aria-label="t('close')"
                    @click="cancelRequestDeleteCustomer()"
                >
                    <X class="w-4 h-4" :stroke-width="2.5" />
                </button>

                <!-- İkon chip -->
                <div class="dcc-icon-chip" aria-hidden="true">
                    <AlertTriangle class="w-6 h-6" :stroke-width="2" />
                </div>

                <!-- Başlık -->
                <h3 class="dcc-title">{{ t('deleteAccountRequest') }}</h3>

                <!-- Açıklama -->
                <p class="dcc-description">{{ t('deleteAccountConfirmText') }}</p>

                <!-- Şifre formu -->
                <form class="dcc-form" @submit.prevent="useDeleteCustomer()">
                    <label class="dcc-label" for="delete-password">
                        {{ t('password') }}
                    </label>
                    <div class="dcc-input-wrap">
                        <input
                            id="delete-password"
                            class="dcc-input"
                            :type="showPassword ? 'text' : 'password'"
                            v-model="password"
                            autocomplete="current-password"
                        />
                        <button
                            type="button"
                            class="dcc-eye"
                            :aria-label="t('password')"
                            @click="showPassword = !showPassword"
                        >
                            <Eye v-if="showPassword" class="w-4 h-4" :stroke-width="2" />
                            <EyeOff v-else class="w-4 h-4" :stroke-width="2" />
                        </button>
                    </div>
                </form>

                <!-- Butonlar -->
                <div class="dcc-actions">
                    <button
                        type="button"
                        class="dcc-btn-danger"
                        @click="useDeleteCustomer()"
                    >
                        <Trash2 class="w-4 h-4" :stroke-width="2.25" />
                        <span>{{ t('confirm') }}</span>
                    </button>
                    <button
                        type="button"
                        class="dcc-btn-secondary"
                        @click="cancelRequestDeleteCustomer()"
                    >
                        <X class="w-4 h-4" :stroke-width="2.25" />
                        <span>{{ t('cancel') }}</span>
                    </button>
                </div>
            </section>
        </Transition>
    </section>
</template>

<style scoped>
.section-delete-customer-happy {
    @apply fixed top-0 left-0 w-full h-full flex justify-center items-start z-30;
}

.delete-customer-overlay {
    @apply absolute inset-0 bg-text-dark-happy-light/50;
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
}

.delete-customer-card {
    @apply relative z-40 w-[20rem] max-w-[calc(100%-1.5rem)] mt-24
           flex flex-col items-center rounded-2xl px-5 pt-8 pb-5 gap-3 overflow-hidden;
    @apply bg-t-card text-t-text ring-1 ring-t-border shadow-2xl;
    @apply happy-ifo-dark:ring-t-danger/30 happy-ifo-dark:shadow-black/40;
}

.dcc-topbar {
    @apply absolute top-0 left-0 right-0 h-1;
    background: linear-gradient(
        90deg,
        rgb(var(--t-danger) / 0.55),
        rgb(var(--t-danger)),
        rgb(var(--t-danger) / 0.55)
    );
}

.dcc-close {
    @apply absolute top-2.5 right-2.5 w-7 h-7 rounded-full
           flex items-center justify-center cursor-pointer
           text-t-text-muted transition-colors duration-150;
}
.dcc-close:hover,
.dcc-close:focus {
    @apply text-t-text bg-t-border/40 outline-none;
}

.dcc-icon-chip {
    @apply w-14 h-14 rounded-full flex items-center justify-center;
    background-color: rgb(var(--t-danger) / 0.15);
    color: rgb(var(--t-danger));
    box-shadow: inset 0 0 0 1px rgb(var(--t-danger) / 0.3);
}

.dcc-title {
    @apply text-center font-semibold text-base leading-snug px-2;
}

.dcc-description {
    @apply text-center text-sm leading-relaxed text-t-text-muted px-1;
}

/* Form */
.dcc-form {
    @apply w-full flex flex-col gap-1.5 mt-1;
}
.dcc-label {
    @apply text-xs font-semibold uppercase tracking-[0.08em] text-t-text-muted;
}
.dcc-input-wrap {
    @apply relative flex items-center;
}
.dcc-input {
    @apply w-full rounded-lg px-3 py-2 pr-9 outline-none transition-colors duration-150
           bg-t-bg text-t-text ring-1 ring-t-border;
}
.dcc-input:focus {
    @apply ring-t-accent ring-2;
}
.dcc-eye {
    @apply absolute right-2 p-1.5 rounded-md text-t-text-muted cursor-pointer transition-colors duration-150;
}
.dcc-eye:hover,
.dcc-eye:focus {
    @apply text-t-text bg-t-border/40 outline-none;
}

/* Butonlar */
.dcc-actions {
    @apply flex flex-col w-full gap-2 mt-2;
}

.dcc-btn-danger {
    @apply flex items-center justify-center gap-2
           font-semibold rounded-lg py-2.5 px-4 w-full
           shadow-md text-white
           transition-all duration-150 cursor-pointer;
    background-color: rgb(var(--t-danger));
}
.dcc-btn-danger:hover,
.dcc-btn-danger:focus {
    background-color: rgb(var(--t-danger) / 0.88);
    @apply shadow-lg outline-none;
    transform: translateY(-1px);
}
.dcc-btn-danger:active {
    background-color: rgb(var(--t-danger) / 0.95);
    @apply shadow-sm;
    transform: translateY(0);
}

.dcc-btn-secondary {
    @apply flex items-center justify-center gap-2
           font-medium rounded-lg py-2.5 px-4 w-full
           bg-transparent text-t-text ring-1 ring-t-border
           transition-all duration-150 cursor-pointer;
}
.dcc-btn-secondary:hover,
.dcc-btn-secondary:focus {
    @apply bg-t-border/25 ring-t-border-strong outline-none;
}
.dcc-btn-secondary:active {
    @apply bg-t-border/40;
}
</style>
